import React, { useEffect, useState, useMemo, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  ChevronRight,
  Warning,
} from '@johnlewispartnership/wtr-ingredients/dist/foundations/icons';
import { useSlotContext } from 'contexts/Slot';

import { getCategoryById, getCategoryBannerById } from 'redux/modules/taxonomy/selectors';
import { getMegaMenuMenus, getMegaMenuActiveLevel } from 'redux/modules/page/selectors/mega-menu';
import { getMegaMenuHistory, getPathForMegaMenuItemById } from 'redux/modules/mega-menu/selectors';
import closeMegaMenu from 'redux/modules/mega-menu/actions/close-mega-menu';
import { GROCERIES_OFFERS, SEASONAL, SLOT_BUTTON, SIGN_OUT } from 'constants/categoryIds';
import { SlotType } from 'services/slotOrchestration/types';
import { urls } from 'constants/urls';

import AnchorLink from 'components/AnchorLink';
import SignOut from 'components/AuthenticationAction/SignOut';
import { LazyIcon } from 'components/LazyIcon/LazyIcon';
import { useMenuExperienceFragments } from 'contexts/MenuExperienceFragments';
import styles from './MenuLink.module.scss';
import { useMenuItemNavigation } from '../hooks/use-menu-item-navigation';

type MenuLinkProps = {
  iconName?: string;
  key?: string;
  categoryIds?: string[];
  emptyOfProducts?: boolean;
  generated?: boolean;
  hiddenInNav?: boolean;
  maxMenus?: number;
  bold?: boolean;
  id: string;
  seasonalMenuTitle?: string;
  url?: string;
  children?: ReactNode;
  name?: string;
  offer?: boolean;
  onClick?: () => void;
  showWarningIcon?: boolean;
};

const MenuLink = ({
  bold,
  iconName,
  id,
  key,
  categoryIds: generatedCategoryIds,
  children,
  emptyOfProducts: generatedEmptyOfProducts,
  generated,
  hiddenInNav: generatedHiddenInNav,
  maxMenus = 3,
  name: generatedName,
  offer = false,
  onClick,
  seasonalMenuTitle,
  showWarningIcon,
  url: generatedUrl,
}: MenuLinkProps) => {
  const dispatch = useDispatch();
  const { slotContext } = useSlotContext();
  const { details: slotDetails } = slotContext;

  const menus = useSelector(getMegaMenuMenus);
  const level = useSelector(getMegaMenuActiveLevel);
  const megaMenuHistory = useSelector(getMegaMenuHistory);
  // @ts-ignore: createSelector known issue, where the return function is typed as receiving a single arg
  const defaultPath = useSelector(state => getPathForMegaMenuItemById(state, id));
  // @ts-ignore: createSelector known issue, where the return function is typed as receiving a single arg
  const banner = useSelector(state => getCategoryBannerById(state, id));
  // @ts-ignore: createSelector known issue, where the return function is typed as receiving a single arg
  const category = useSelector(state => getCategoryById(state, id)) ?? {};

  const categoryIds = generated ? generatedCategoryIds : category?.categoryIds || [];
  const hasDescendants = categoryIds?.length > 0 && megaMenuHistory?.length < maxMenus;
  const {
    iconName: icon,
    name: categoryName,
    offer: categoryOffer,
    url: categoryUrl,
    emptyOfProducts: categoryEmptyOfProducts,
    hiddenInNav: categoryHiddenInNav,
  } = category;
  const isOffer = categoryOffer || false;
  const hiddenInNav = generated ? generatedHiddenInNav : categoryHiddenInNav;
  const emptyOfProducts = generated ? generatedEmptyOfProducts : categoryEmptyOfProducts;
  const display = !emptyOfProducts && !hiddenInNav;

  const [finalIcon, setFinalIcon] = useState(generated ? iconName : icon);
  const [name, setName] = useState(generated ? generatedName : categoryName);
  const [path, setPath] = useState(generated ? generatedUrl : (categoryUrl ?? defaultPath));
  const hasBookedSlot = slotDetails?.startDateTime && slotDetails?.slotType !== SlotType.UNDEFINED;

  const changeSlotUrl = useMemo(() => {
    switch (slotDetails?.slotType) {
      case SlotType.DELIVERY:
        return urls.bookDeliverySlot;
      case SlotType.ENTERTAINING_COLLECTION:
        return urls.bookEntertainingCollectionSlot;
      case SlotType.GROCERY_COLLECTION:
        return urls.bookGroceriesAndEntertainingCollectionSlot;
      default:
        return urls.serviceSelection;
    }
  }, [slotDetails?.slotType]);

  const isSignOut = id === SIGN_OUT;
  const isLink = (path && !hasDescendants) ?? false;
  const { handleClickItem, handleKeyDownItem, ref, tabIndex } = useMenuItemNavigation({
    display,
    hasDescendants,
    id,
    isLink,
    level,
    menus,
    name,
    onClick,
    closeMegaMenu: () => dispatch(closeMegaMenu()),
    newWindow: false,
  });

  useEffect(() => {
    if (id === GROCERIES_OFFERS) {
      setName('Groceries OFFERS');
    }

    if (id === SLOT_BUTTON && !hasBookedSlot) {
      setName('Book Slot');
    }

    if (id === SLOT_BUTTON && hasBookedSlot) {
      setFinalIcon('CalendarBooked');
      setName('Change Slot');
      setPath(changeSlotUrl);
    }

    if (id === SEASONAL && seasonalMenuTitle) {
      setName(seasonalMenuTitle);
    }
  }, [changeSlotUrl, hasBookedSlot, id, seasonalMenuTitle]);

  const { fetchAndStoreFragment } = useMenuExperienceFragments();

  if (!display) return null;

  const testName = `${name ? `${name}-` : ''}menu-link-${id}`;
  const classes = classNames({
    [styles.bold]: bold,
    [styles.offer]: offer || isOffer,
    [styles.showWarningIcon]: showWarningIcon,
  });
  const contents = (
    <>
      <div className={styles.nameWrapper}>
        {finalIcon && <LazyIcon name={finalIcon} className={styles.icon} size="small" />}
        {
          // eslint-disable-next-line react/jsx-no-useless-fragment
          children && <>{children}</>
        }
        {!children && name && <span className={styles.name}>{name}</span>}
        {showWarningIcon && <Warning className={styles.warningIcon} data-testid="warning-icon" />}
      </div>
      {hasDescendants && <ChevronRight className={styles.descendantsIcon} size="small" />}
    </>
  );

  if (isSignOut) {
    return (
      <SignOut
        key={key}
        className={classNames(styles.signOut, styles.link, classes)}
        data-testid={testName}
      />
    );
  }

  if (isLink) {
    return (
      <AnchorLink
        key={key}
        className={classNames(styles.link, classes)}
        data-actiontype="redirect"
        data-origincomponent="mobile megamenu link"
        data-shortdescription={name || children}
        ref={ref}
        onClick={handleClickItem}
        onKeyDown={handleKeyDownItem}
        tabIndex={Number(tabIndex)}
        to={path}
        data-testid={testName}
      >
        {contents}
      </AnchorLink>
    );
  }
  return (
    <button
      key={key}
      className={classNames(styles.button, classes)}
      onClick={event => {
        handleClickItem(event);
        if (hasDescendants && banner) {
          fetchAndStoreFragment(banner);
        }
      }}
      onKeyDown={handleKeyDownItem}
      tabIndex={Number(tabIndex)}
      type="button"
      data-testid={testName}
    >
      {contents}
    </button>
  );
};

MenuLink.displayName = 'MenuLink';

export default MenuLink;
